import React, { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Modal, { ModalContent, ModalHeading, ModalSize } from 'components/modal';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import { useFlag } from 'state/launchdarkly';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

import { ItemNutrition } from './item-nutrition';
import { NutritionDisclaimer } from './styled';
import { INutritionProps } from './types';

const Nutrition: React.FC<INutritionProps> = ({ nutritionFacts, activatingElement }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { formatMessage } = useIntl();
  const { featureDisclaimers } = useFeatureDisclaimers();
  const { calorie } = featureDisclaimers || {};

  const enableFeatureNutritionIntegration = useFlag(
    LaunchDarklyFlag.ENABLE_FEATURE_NUTRITION_INTEGRATION
  );
  const enableIngredients = useFlag(LaunchDarklyFlag.ENABLE_PRODUCT_INGREDIENTS);
  const enableStaticMenuAllergens = useFlag(LaunchDarklyFlag.ENABLE_STATIC_MENU_ALLERGENS);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (isModalOpen && headingRef.current) {
      headingRef.current.focus();
    }
  }, [isModalOpen]);

  const modalHeading = formatMessage({
    id: enableIngredients ? 'nutritionalAndIngredients' : 'nutritionalInformation',
  });
  return (
    <>
      {React.cloneElement(activatingElement, { onClick: openModal })}
      {isModalOpen && (
        <Modal size={ModalSize.SMALL} onDismiss={closeModal} data-testid="nutrition-modal">
          <ModalContent>
            <ModalHeading ref={headingRef} tabIndex={-1} id="modal-heading">
              {modalHeading}
            </ModalHeading>
            {nutritionFacts
              .filter(
                elem =>
                  elem.itemName.toLowerCase() !== 'combo item' &&
                  elem.itemName.toLowerCase() !== 'mix n match'
              )
              .map(({ itemName, itemImage, facts, allergenGroups, additionalItemInformation }) => (
                <ItemNutrition
                  key={itemName}
                  allergenGroups={allergenGroups}
                  itemImage={itemImage}
                  itemName={itemName}
                  nutritionInfo={facts}
                  enableFeatureNutritionIntegration={enableFeatureNutritionIntegration}
                  enableStaticMenuAllergens={enableStaticMenuAllergens}
                  additionalItemInformation={additionalItemInformation}
                />
              ))}
            <NutritionDisclaimer content={calorie?.localeRaw} />
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default Nutrition;
